import React, { useState, useEffect } from 'react';
import { authService } from '../services/auth';
import {
  MissionControlIcon,
  RefreshCwIcon,
  BookIcon,
  HeartIcon,
  CalendarIcon,
  TargetIcon,
  CheckSquareIcon,
  ChecklistIcon,
  PieChartIcon,
  TrendingUpIcon,
  TrendingDownIcon,
  DollarSignIcon,
  FileTextIcon,
  BarChartIcon,
  TagIcon,
  WalletIcon,
  LineChartIcon,
  SettingsIcon,
  MessageCircleIcon,
  ChevronDownIcon,
  XIcon,
  UtensilsIcon,
  RunningIcon,
  ScaleIcon,
  FlameIcon,
  UsersIcon,
  ActivityIcon,
  BriefcaseIcon,
} from './Icons';
import './Sidebar.css';

// Create icon elements for nav items
const icons = {
  missionControl: <MissionControlIcon />,
  routines: <RefreshCwIcon />,
  journal: <BookIcon />,
  health: <HeartIcon />,
  calendar: <CalendarIcon />,
  goals: <TargetIcon />,
  tasks: <CheckSquareIcon />,
  checklists: <ChecklistIcon />,
  budget: <PieChartIcon />,
  income: <TrendingUpIcon />,
  expenses: <TrendingDownIcon />,
  savings: <DollarSignIcon />,
  transactions: <FileTextIcon />,
  reports: <BarChartIcon />,
  categories: <TagIcon />,
  reserves: <WalletIcon />,
  analytics: <LineChartIcon />,
  productAnalytics: <BarChartIcon />,
  feedback: <MessageCircleIcon />,
  chevron: <ChevronDownIcon size={16} />,
  admin: <SettingsIcon />,
  barChart: <BarChartIcon />,
  book: <BookIcon />,
  utensils: <UtensilsIcon />,
  running: <RunningIcon />,
  scale: <ScaleIcon />,
  flame: <FlameIcon />,
  users: <UsersIcon />,
  activity: <ActivityIcon />,
  cms: <FileTextIcon />,
  personalOS: <BriefcaseIcon />,
  budgetOS: <WalletIcon />,
};

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
}

interface SidebarProps {
  activeItem: string;
  onNavigate: (itemId: string) => void;
  isOpen?: boolean;
  onClose?: () => void;
}

const personalOSNavItems: NavItem[] = [
  { id: 'routines', label: 'Routines', icon: icons.routines },
  { id: 'journal', label: 'Journal', icon: icons.journal },
  { id: 'calendar', label: 'Calendar', icon: icons.calendar },
  { id: 'goals', label: 'Vision, Goals and Projects', icon: icons.goals },
  { id: 'tasks', label: 'Tasks', icon: icons.tasks },
  { id: 'checklists', label: 'Checklists', icon: icons.checklists },
];

const healthNavItems: NavItem[] = [
  { id: 'health-overview', label: 'Med Ops', icon: icons.barChart },
  { id: 'health-journal', label: 'Health Journal', icon: icons.book },
  { id: 'health-meal-planning', label: 'Meal Planning', icon: icons.utensils },
  { id: 'health-exercise', label: 'Exercise', icon: icons.running },
  { id: 'health-measurements', label: 'Measurements', icon: icons.scale },
];

const budgetOSNavItems: NavItem[] = [
  { id: 'budget-os-budget', label: 'Budget', icon: icons.budget },
  { id: 'budget-os-income', label: 'Income', icon: icons.income },
  { id: 'budget-os-expenses', label: 'Expenses', icon: icons.expenses },
  { id: 'budget-os-savings', label: 'Savings & Investments', icon: icons.savings },
  { id: 'budget-os-investment-journal', label: 'Investment Journal', icon: icons.book },
  { id: 'budget-os-transactions', label: 'Transactions', icon: icons.transactions },
  { id: 'budget-os-reports', label: 'Reports', icon: icons.reports },
];

const adminNavItems: NavItem[] = [
  { id: 'admin', label: 'Admin Dashboard', icon: icons.admin },
  { id: 'admin-analytics', label: 'Analytics & Metrics', icon: icons.barChart },
  { id: 'analytics-dashboard', label: 'Product Analytics', icon: icons.productAnalytics },
  { id: 'feedback-dashboard', label: 'Feedback Management', icon: icons.feedback },
  { id: 'support-tickets', label: 'Support Tickets', icon: icons.feedback },
  { id: 'email-templates', label: 'Email Templates', icon: icons.transactions },
  { id: 'knowledge-base', label: 'Knowledge Base (Admin)', icon: icons.tasks },
  { id: 'admin-users', label: 'Users', icon: icons.users },
  { id: 'admin-activity', label: 'Activity', icon: icons.activity },
  { id: 'admin-cms', label: 'CMS', icon: icons.cms },
];

const Sidebar: React.FC<SidebarProps> = ({ activeItem, onNavigate, isOpen = false, onClose }) => {
  // Check if active item belongs to a specific accordion
  const isBudgetOSItem = activeItem.startsWith('budget-os');
  const isHealthItem = activeItem.startsWith('health-');
  const isPersonalOSItem = personalOSNavItems.some(item => item.id === activeItem);
  const isAdminItem = adminNavItems.some(item => item.id === activeItem);

  const [isPersonalOSOpen, setIsPersonalOSOpen] = useState(false);
  const [isHealthOpen, setIsHealthOpen] = useState(false);
  const [isBudgetOSOpen, setIsBudgetOSOpen] = useState(false);
  const [isAdminOpen, setIsAdminOpen] = useState(false);

  // Keep accordions open when their items are active
  useEffect(() => {
    if (isBudgetOSItem) {
      setIsBudgetOSOpen(true);
    }
    if (isHealthItem) {
      setIsHealthOpen(true);
    }
    if (isPersonalOSItem) {
      setIsPersonalOSOpen(true);
    }
    if (isAdminItem) {
      setIsAdminOpen(true);
    }
  }, [activeItem, isBudgetOSItem, isHealthItem, isPersonalOSItem, isAdminItem]);

  // Handle navigation and close sidebar on mobile
  const handleNavigate = (itemId: string) => {
    onNavigate(itemId);
    // Close sidebar on mobile after navigation
    if (window.innerWidth <= 768 && onClose) {
      onClose();
    }
  };

  // Close sidebar when clicking overlay
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && onClose) {
      onClose();
    }
  };

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && <div className="sidebar-overlay" onClick={handleOverlayClick} />}
      <aside className={`sidebar ${isOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
        {/* Close button for mobile */}
        {onClose && (
          <button className="sidebar-close-button" onClick={onClose} aria-label="Close menu">
            <XIcon size={24} />
          </button>
        )}
        {/* Mission Control - Standalone at top */}
        <button
          className={`sidebar-brand dashboard-brand ${activeItem === 'dashboard' ? 'active' : ''}`}
          onClick={() => handleNavigate('dashboard')}
        >
          <span className="nav-icon">{icons.missionControl}</span>
          <h1 className="sidebar-title">Mission Control</h1>
        </button>

        {/* Personal OS Accordion */}
        <button
          className={`sidebar-brand ${isPersonalOSOpen ? 'expanded' : ''}`}
          onClick={() => setIsPersonalOSOpen(!isPersonalOSOpen)}
          aria-expanded={isPersonalOSOpen}
        >
          <span className="nav-icon">{icons.personalOS}</span>
          <h1 className="sidebar-title">Personal</h1>
          <span className="accordion-icon">{icons.chevron}</span>
        </button>
        <nav className="sidebar-nav">
          {isPersonalOSOpen && (
            <ul className="nav-list">
              {personalOSNavItems.map((item) => (
                <li key={item.id} className="nav-item">
                  <button
                    className={`nav-button ${activeItem === item.id ? 'active' : ''}`}
                    onClick={() => handleNavigate(item.id)}
                  >
                    <span className="nav-icon">{item.icon}</span>
                    <span className="nav-label">{item.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* Health - Accordion section */}
          <button
            className={`sidebar-brand health-brand ${isHealthOpen ? 'expanded' : ''}`}
            onClick={() => setIsHealthOpen(!isHealthOpen)}
            aria-expanded={isHealthOpen}
          >
            <span className="nav-icon">{icons.health}</span>
            <h1 className="sidebar-title">Health</h1>
            <span className="accordion-icon">{icons.chevron}</span>
          </button>

          {isHealthOpen && (
            <ul className="nav-list">
              {healthNavItems.map((item) => (
                <li key={item.id} className="nav-item">
                  <button
                    className={`nav-button ${activeItem === item.id ? 'active' : ''}`}
                    onClick={() => handleNavigate(item.id)}
                  >
                    <span className="nav-icon">{item.icon}</span>
                    <span className="nav-label">{item.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          <button
            className={`sidebar-brand budget-os-brand ${isBudgetOSOpen ? 'expanded' : ''}`}
            onClick={() => setIsBudgetOSOpen(!isBudgetOSOpen)}
            aria-expanded={isBudgetOSOpen}
          >
            <span className="nav-icon">{icons.budgetOS}</span>
            <span className="sidebar-title">Budget</span>
            <span className="accordion-icon">{icons.chevron}</span>
          </button>

          {isBudgetOSOpen && (
            <ul className="nav-list">
              {budgetOSNavItems.map((item) => (
                <li key={item.id} className="nav-item">
                  <button
                    className={`nav-button ${activeItem === item.id ? 'active' : ''}`}
                    onClick={() => handleNavigate(item.id)}
                  >
                    <span className="nav-icon">{item.icon}</span>
                    <span className="nav-label">{item.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* Analytics - Standalone below Budget OS */}
          <button
            className={`sidebar-brand analytics-brand ${activeItem === 'analytics' ? 'active' : ''}`}
            onClick={() => handleNavigate('analytics')}
          >
            <span className="nav-icon">{icons.analytics}</span>
            <h1 className="sidebar-title">Analytics</h1>
          </button>

          {/* Admin Accordion - only visible to admins */}
          {authService.getCurrentUser()?.isAdmin && (
            <>
              <button
                className={`sidebar-brand admin-brand ${isAdminOpen ? 'expanded' : ''}`}
                onClick={() => setIsAdminOpen(!isAdminOpen)}
                aria-expanded={isAdminOpen}
              >
                <span className="nav-icon">{icons.admin}</span>
                <span className="sidebar-title">Admin</span>
                <span className="accordion-icon">{icons.chevron}</span>
              </button>
              {isAdminOpen && (
                <ul className="nav-list">
                  {adminNavItems.map((item) => (
                    <li key={item.id} className="nav-item">
                      <button
                        className={`nav-button ${activeItem === item.id ? 'active' : ''}`}
                        onClick={() => handleNavigate(item.id)}
                      >
                        <span className="nav-icon">{item.icon}</span>
                        <span className="nav-label">{item.label}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </nav>
      </aside>
    </>
  );
};

export default Sidebar;
